import { AddCircleOutline } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateStorageCache, useCreateStorages, useDeleteStorages, useGetStorages } from 'src/api/storage'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import StorageEditContainer from 'src/containers/storage/StorageEditContainer'
import StorageFormDialog from 'src/containers/storage/StorageFormDialog'
import StoragesDetailPanel from 'src/containers/storage/StoragesDetailPanel'
import StorageTable from 'src/containers/storage/StorageTable'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'

const StoragesPage = () => {
  const { t } = useTranslation('storage')
  const { id } = useParams()
  const { data, isLoading } = useGetStorages(id as EntityId)
  const { mutateAsync: createStorage, isLoading: submitLoading } = useCreateStorages(id as EntityId)
  const { mutateAsync: removeStorage } = useDeleteStorages(id as EntityId)
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const theme = useTheme()

  const onRemoveStorage = useCallback(
    async (storageId: EntityId) => {
      showConfirmDialog(t('deleteStorage'), t('areYouSureDeleteStorage'), {}, async pop => {
        await removeStorage(storageId, {
          onSuccess: () => {
            toast.success(t('storage-deleted-successfully'), {})
            invalidateCustomer.getCustomerUsersForAdmins(id, true)
            invalidateStorageCache.getStorageForDropDown(id as EntityId)
            invalidateStorageCache.getStorages(id as EntityId)
            invalidateCustomer.getStorageCustomersByUser()
            invalidateStorageCache.getAllStorageWithCustomers(id as EntityId)
            pop()
          },
          onError: error => {
            if (error && error['data'] === 'ComponentsExists') {
              toast.error(t('components-exist'))
            }
          },
        })
      })
    },
    [removeStorage, id, showConfirmDialog],
  )

  const onUpdateStorage = initialValues => {
    showDialog(
      StorageEditContainer,
      {
        componentProps: {
          customerId: id,
          isLoading: submitLoading,

          initialValues: initialValues,
        },
        opts: { maxWidth: 'md', fullWidth: true },
      },
      undefined,
      true,
    )
  }

  const onAddStorage = () => {
    showDialog(
      StorageFormDialog,
      {
        componentProps: {
          customerId: id,
          isLoading: submitLoading,
          onSubmit: async (form, pop) => {
            form.customerId = id
            await createStorage(form, {
              onSuccess: () => {
                toast.success(t('storage-added-successfully'))
                invalidateStorageCache.getStorages(id as EntityId)
                invalidateCustomer.getStorageCustomersByUser()
                invalidateStorageCache.getAllStorageWithCustomers(id as EntityId)
                invalidateStorageCache.getStorageForDropDown(id as EntityId)
                pop()
              },
            })
          },
        },
        opts: { maxWidth: 'md', fullWidth: true },
      },
      undefined,
      true,
    )
  }

  return (
    <CommonPageLayout
      titleSection={{ title: t('storages') }}
      topAction={[
        <GridActionButton
          key="add"
          startIcon={<AddCircleOutline />}
          buttonText={t('storage')}
          onClick={() => onAddStorage()}
        />,
      ]}
    >
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <StorageTable
          data={data}
          isLoading={isLoading}
          handleEdit={onUpdateStorage}
          handleRemove={onRemoveStorage}
          onDetailsPanel={({ row }) => {
            return <StoragesDetailPanel storageId={row?.id} storageName={row?.name} />
          }}
        />
      </Box>
    </CommonPageLayout>
  )
}

export default StoragesPage
